import {
  getOrderDataFromLocalStorage,
  removeOrderDataFromLocalStorage,
} from '../util/localStorage';
import { createVariantFromProductAttrs } from '../util/productVariantCreator';
import { trackAnalyticsError } from './newRelicTrackingApi';

// Tracking page and event name strings.
import {
  BUTTON_CLICKED_EVENT_NAME,
  BUY_IT_AGAIN_PAGE_NAME,
  BUY_IT_AGAIN_PAGE_ZONE,
  CHECKOUT_PAGE_STAGE,
  CONFIRMATION_PAGE_SECTION,
  DESIGN_TECH_LEGACY_MODAL_ERROR,
  DESIGN_TECH_LEGACY_MODAL_UNAVAILABLE,
  EDIT_DESIGN_AND_REORDER_CTA_VALUE,
  EXPRESS_CHECKOUT_ADDED_TO_CART_EVENT_NAME,
  ITEM_LEGACY_DOC_CONVERSION_ATTEMPTED_EVENT_NAME,
  LEGACY_DOCUMENT_DESIGN_TECH_DIFY_REMEDIATION_ATTEMPTED,
  LEGACY_DOCUMENT_DESIGN_TECH_DIFY_REMEDIATION_FAILED,
  LEGACY_DOCUMENT_DESIGN_TECH_DIFY_REMEDIATION_SUCCEEDED,
  LEGACY_DOCUMENT_REMEDIATION_MODAL_PRESENTED_TO_CUSTOMER,
  LEGACY_EDIT_DOCUMENT_URL_CONVERSION_SUCCESS_EVENT_NAME,
  MY_ACCOUNT_PAGE_SECTION,
  MY_ACCOUNT_PAGE_STAGE,
  NAVIGATION_CLICKED_EVENT_NAME,
  ORDER_COMPLETED_SEGMENT_EVENT_NAME,
  ORDER_CONFIRMATION_PAGE_NAME,
  ORDER_DETAILS_PAGE_NAME,
  ORDER_FIND_MY_ORDER_PAGE_NAME,
  ORDER_HISTORY_PAGE_NAME,
  ORDER_ITEMS_REORDERED_ATTEMPTED_EVENT_NAME,
  ORDER_VAT_INVOICE_AVAILABLE_ERROR,
  ORDER_VAT_INVOICE_DOWNLOAD_ATTEMPT_FAILED_EVENT_NAME,
  ORDER_VAT_INVOICE_DOWNLOAD_ATTEMPT_SUCCEEDED_EVENT_NAME,
  ORDER_VAT_INVOICE_DOWNLOAD_ATTEMPTED_EVENT_NAME,
  PRODUCT_REORDERED_LABEL,
  REORDER_PRODUCT_ADDED_TO_CART_EVENT_NAME,
  STUDIO_DESTINATION_PAGE_PATH,
  TRACKING_URL_CLICKED_LABEL,
  TRACKING_URL_LINK_DETAIL,
} from '../constants/orderdetailsui';

/**
 * Protect against tracking not yet being installed on page or window not existing (SSR).
 * The `tracking.js` package will fire a `trackingReady` event when tracking is
 * on the page.  Each `callWhenReadyFn` is a single-shot and removed once executed.
 * Note we use this to check for both 'track' and 'page' functions, since they in the same module.
 */
function onTrackingReady(callback) {
  if (window) {
    if (window.tracking && window.tracking.track) {
      callback();
    } else {
      const callWhenReadyFn = () => {
        callback();
        window.removeEventListener('trackingReady', callWhenReadyFn);
      };
      window.addEventListener('trackingReady', callWhenReadyFn);
    }
  }
}

/**
 * Actually send `track` event. Assumes `window.tracking.track` exists.
 */
function sendTrackingEvent(name, data) {
  // eslint-disable-next-line camelcase
  const shopperId = data.shopperId || data.shopper_id || '';
  try {
    window.tracking.track(name, data);
  } catch (error) {
    trackAnalyticsError(error, shopperId, data);
    console.error(
      "[TrackingJS] Error occured when firing a 'track' event",
      error
    );
  }
}

/**
 * Actually send `page` event. Assumes `window.tracking.page` exists.
 */
function sendPageEvent(
  pageTrackingName,
  pageTrackingSectionName,
  pageTrackingStageName,
  shopperId,
  additionalData
) {
  const pageData = {
    pageName: pageTrackingName,
    pageSection: pageTrackingSectionName,
    pageStage: pageTrackingStageName,
    shopperId,
    additionalData,
  };
  try {
    window.tracking.page(pageData);
  } catch (error) {
    trackAnalyticsError(error, shopperId, pageData);
    console.error(
      "[TrackingJS] Error occured when firing a 'page' event",
      error
    );
  }
}

/** Execute or queue up a track call */
function track(name, data) {
  onTrackingReady(() => sendTrackingEvent(name, data));
}

/** Execute or queue up a 'page' tracking call. */
export function pageViewed(
  pageTrackingName,
  pageTrackingSectionName,
  pageTrackingStageName,
  shopperId,
  additionalData
) {
  onTrackingReady(() =>
    sendPageEvent(
      pageTrackingName,
      pageTrackingSectionName,
      pageTrackingStageName,
      shopperId,
      additionalData
    )
  );
}

// TODO: Revisit this for NRQL integration.
// Could be used to track # of orders canceled, reorders placed, and any other actions a user can take on the page.
export const trackOrderAction = (actionName, additionalData) => {
  track(actionName, additionalData);
};

/**
 * If order data exists in local storage for the provided orderId,
 * emit a track event for that data and then remove it from local storage.
 * If no order data exists in local storage, then do nothing.
 * This scenario is in place to allow us to report an order placed event when an order is completed during
 * a payment redirect flow (3DS, paypal, etc.) and we were not able to properly emit the placed order event
 * from the checkout UI.
 * @param {*} orderId
 */
export function orderPlacedEventIfDataPresent(orderId) {
  const placedOrderEventData = getOrderDataFromLocalStorage(orderId);
  if (placedOrderEventData) {
    if (!placedOrderEventData.label) {
      placedOrderEventData.label = 'Regular flow';
    }
    track(ORDER_COMPLETED_SEGMENT_EVENT_NAME, placedOrderEventData);
    removeOrderDataFromLocalStorage(orderId);
  }
}

/**
 * Fire an event that states this orderNumber was attempted to be reordered, with these lineItems being part of
 * that attempt.
 *
 * This method will fire an event for EACH lineItem ID
 * @param {string} orderNum
 * @param {array} lineItemIds
 */
export function orderReorderAttempted(orderNum, lineItemIds) {
  if (lineItemIds) {
    lineItemIds.forEach(id => {
      const reorderData = {
        orderNumber: orderNum,
        lineItemId: id,
      };
      trackOrderAction(ORDER_ITEMS_REORDERED_ATTEMPTED_EVENT_NAME, reorderData);
    });
  }
}

/**
 * Fire an event that states this orderNumber was attempted to be reordered, with these lineItems being part of
 * that attempt.
 *
 * This method will fire an event for EACH lineItem ID
 * @param {string} orderNum
 * @param {array} lineItemIds
 */
export function orderReorderV2Attempted(orderNum, reorderItems) {
  if (reorderItems) {
    reorderItems.forEach(reorderItem => {
      const reorderItemData = {
        orderNumber: orderNum,
        lineItemId: reorderItem.lineItemId,
        quantity: reorderItem.quantity,
      };
      trackOrderAction(
        ORDER_ITEMS_REORDERED_ATTEMPTED_EVENT_NAME,
        reorderItemData
      );
    });
  }
}

/**
 * Fire an event that states that converting a legacy document was attempted
 *
 * @param {object} additionalData
 */
export function convertLegacyDocumentAttempted(additionalData) {
  trackOrderAction(
    ITEM_LEGACY_DOC_CONVERSION_ATTEMPTED_EVENT_NAME,
    additionalData
  );
}

/**
 * Fire an event that states that a VAT invoice download was attempted
 *
 * @param {object} additionalData
 */
export function vatInvoiceDownloadAttempted(additionalData) {
  trackOrderAction(
    ORDER_VAT_INVOICE_DOWNLOAD_ATTEMPTED_EVENT_NAME,
    additionalData
  );
}

/**
 * Fire an event that states that a VAT invoice download was attempted
 *
 * @param {object} additionalData
 */
export function vatInvoiceAvailableError(additionalData) {
  trackOrderAction(ORDER_VAT_INVOICE_AVAILABLE_ERROR, additionalData);
}

/**
 * Fire an event that states that a VAT invoice download attempt succeeded
 *
 * @param {object} additionalData
 */
export function vatInvoiceDownloadAttemptSucceeded(additionalData) {
  trackOrderAction(
    ORDER_VAT_INVOICE_DOWNLOAD_ATTEMPT_SUCCEEDED_EVENT_NAME,
    additionalData
  );
}

/**
 * Fire an event that states that a VAT invoice download attempt failed
 *
 * @param {object} additionalData
 */
export function vatInvoiceDownloadAttemptFailed(additionalData) {
  trackOrderAction(
    ORDER_VAT_INVOICE_DOWNLOAD_ATTEMPT_FAILED_EVENT_NAME,
    additionalData
  );
}

/**
 * Fire an event that states the legacy reorder Design Service threw an error
 *
 * @param {object} additionalData
 */
export function legacyDocumentRemediationDesignServiceError(additionalData) {
  trackOrderAction(DESIGN_TECH_LEGACY_MODAL_ERROR, additionalData);
}

/**
 * Fire an event that states the legacy  Design Service was unavailable
 *
 * @param {object} additionalData
 */
export function legacyDocumentRemediationDesignServiceUnavailable(
  additionalData
) {
  trackOrderAction(DESIGN_TECH_LEGACY_MODAL_UNAVAILABLE, additionalData);
}

/**
 * Fire an event that states a customer clicked to have design services update their legacy design
 *
 * @param {object} additionalData
 */
export function documentConversionFailureDesignServiceRemediationAttempted(
  additionalData
) {
  trackOrderAction(
    LEGACY_DOCUMENT_DESIGN_TECH_DIFY_REMEDIATION_ATTEMPTED,
    additionalData
  );
}

/**
 * Fire an event that states a customer's request to have design services update their legacy design succeeded
 *
 * @param {object} additionalData
 */
export function documentConversionFailureDesignServiceRemediationSucceeded(
  additionalData
) {
  trackOrderAction(
    LEGACY_DOCUMENT_DESIGN_TECH_DIFY_REMEDIATION_SUCCEEDED,
    additionalData
  );
}

/**
 * Fire an event that states a customer's request to have design services update their legacy design failed
 *
 * @param {object} additionalData
 */
export function documentConversionFailureDesignServiceRemediationFailed(
  additionalData
) {
  trackOrderAction(
    LEGACY_DOCUMENT_DESIGN_TECH_DIFY_REMEDIATION_FAILED,
    additionalData
  );
}

/**
 * Fire an event that states a customer was presented with an option to fix their
 * failed legacy doc
 *
 * @param {object} additionalData
 */
export function docRemediationModalPresentedToCustomer(additionalData) {
  trackOrderAction(
    LEGACY_DOCUMENT_REMEDIATION_MODAL_PRESENTED_TO_CUSTOMER,
    additionalData
  );
}

export function pageViewOrderDetails(shopperId) {
  pageViewed(
    ORDER_DETAILS_PAGE_NAME,
    MY_ACCOUNT_PAGE_SECTION,
    MY_ACCOUNT_PAGE_STAGE,
    shopperId
  );
}

export function pageViewOrderConfirmation(shopperId) {
  pageViewed(
    ORDER_CONFIRMATION_PAGE_NAME,
    CONFIRMATION_PAGE_SECTION,
    CHECKOUT_PAGE_STAGE,
    shopperId
  );
}

export function pageViewOrderHistory(shopperId) {
  pageViewed(
    ORDER_HISTORY_PAGE_NAME,
    MY_ACCOUNT_PAGE_SECTION,
    MY_ACCOUNT_PAGE_STAGE,
    shopperId
  );
}

export function pageViewBuyItAgain(shopperId) {
  pageViewed(
    BUY_IT_AGAIN_PAGE_NAME,
    MY_ACCOUNT_PAGE_SECTION,
    MY_ACCOUNT_PAGE_STAGE,
    shopperId
  );
}

export function pageViewFindMyOrder(shopperId) {
  pageViewed(
    ORDER_FIND_MY_ORDER_PAGE_NAME,
    MY_ACCOUNT_PAGE_SECTION,
    MY_ACCOUNT_PAGE_STAGE,
    shopperId
  );
}

export function trackAnonymousShopperSignedIn(anonymousShopperId) {
  track('Anonymous Shopper Signed In', { anonymousId: anonymousShopperId });
}

/**
 * Fire an event that tracks when a user clicks the reorder button
 * occurs after the re-order comes back as a successful operation (i.e. item has been added to cart successfully).
 *
 * @param {object} additionalData
 */
export function trackReorderProductAddedToCart(reorderableItem, sourcePage) {
  const additionalData = {
    label: PRODUCT_REORDERED_LABEL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName: `My Account: ${sourcePage}`,
    product_id: reorderableItem?.purcsData?.mpvId || reorderableItem.id,
    originalLineItemId: reorderableItem.id,
    name: reorderableItem.productName,
    core_product_id: reorderableItem.productKey,
    is_reordered: true,
    price: reorderableItem.totalDiscountedPrice?.centAmount,
    list_price: reorderableItem.totalListPrice?.centAmount,
    quantity: 1,
    sales_quantity: reorderableItem.quantity,
    discount:
      typeof reorderableItem?.totalListPrice?.centAmount === 'number' &&
      typeof reorderableItem?.totalDiscountedPrice?.centAmount === 'number'
        ? reorderableItem.totalListPrice.centAmount -
          reorderableItem.totalDiscountedPrice.centAmount
        : null,
    variant: createVariantFromProductAttrs(
      reorderableItem.displayProductAttributes
    ),
  };

  trackOrderAction(REORDER_PRODUCT_ADDED_TO_CART_EVENT_NAME, additionalData);
}

/**
 * Fire an event that tracks when a user clicks the reorder button from the "Buy It Again Page"
 * occurs after the re-order comes back as a successful operation (i.e. item has been added to cart successfully).
 *
 * @param {object} additionalData
 */
export function trackBuyItAgainReorderProductAddedToCart({
  item,
  pricedQuantity,
}) {
  const additionalData = {
    label: PRODUCT_REORDERED_LABEL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName: BUY_IT_AGAIN_PAGE_NAME,
    product_id: item.purcsData?.mpvId || item.id,
    originalLineItemId: item.id,
    name: item.productData?.defaultDisplayName,
    core_product_id: item.productData?.productKey,
    is_reordered: true,
    // TODO - we may want to have decimal prices instead of cent amount here; for now this is fine, but we will verify with Hasnene
    price: pricedQuantity.discountedPriceCents?.untaxed,
    list_price: pricedQuantity.listPriceCents?.untaxed,
    quantity: 1,
    sales_quantity: pricedQuantity.quantity,
    discount:
      typeof pricedQuantity?.listPriceCents?.untaxed === 'number' &&
      typeof pricedQuantity?.discountedPriceCents?.untaxed === 'number'
        ? pricedQuantity.listPriceCents.untaxed -
          pricedQuantity.discountedPriceCents.untaxed
        : null,
    variant: createVariantFromProductAttrs(
      item.productData?.displayProductAttributes
    ),
  };

  trackOrderAction(REORDER_PRODUCT_ADDED_TO_CART_EVENT_NAME, additionalData);
}

/**
 * Fire an event that tracks when a user clicks the 'Edit design & reorder' button from the "Buy It Again Page" and is navigated to studio
 *
 * @param {object} additionalData
 */
export function trackBuyItAgainEditDesignAndReorderClicked() {
  const additionalData = {
    category: MY_ACCOUNT_PAGE_SECTION,
    label: 'Content: Buy It Again',
    eventDetail: `${MY_ACCOUNT_PAGE_SECTION};${STUDIO_DESTINATION_PAGE_PATH};${BUY_IT_AGAIN_PAGE_ZONE};${EDIT_DESIGN_AND_REORDER_CTA_VALUE}`,
    navigationDetail: EDIT_DESIGN_AND_REORDER_CTA_VALUE,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName: BUY_IT_AGAIN_PAGE_NAME,
  };

  trackOrderAction(NAVIGATION_CLICKED_EVENT_NAME, additionalData);
}

/**
 * Fire an event that tracks when a user clicks the edit button for a migrated order
 * occurs after the doc conversion call comes back as a successful operation (i.e. editDocumentUrl is present on the response).
 *
 * @param {object} additionalData
 */
export function trackLegacyEditDocumentUrlConversionSuccess(
  lineItem,
  sourcePage
) {
  const additionalData = {
    label: 'Legacy Edit URL returned from DocGateway',
    pageSection: 'My Account',
    pageStage: 'My Account',
    pageName: `My Account: ${sourcePage}`,
    product_id: lineItem.id,
    name: lineItem.productName,
    core_product_id: lineItem.productKey,
    price: lineItem.totalDiscountedPrice?.centAmount,
    list_price: lineItem.totalListPrice?.centAmount,
    quantity: 1,
    sales_quantity: lineItem.quantity,
    discount:
      typeof lineItem?.totalListPrice?.centAmount === 'number' &&
      typeof lineItem?.totalDiscountedPrice?.centAmoun === 'number'
        ? lineItem.totalListPrice.centAmount -
          lineItem.totalDiscountedPrice.centAmount
        : null,
    variant: createVariantFromProductAttrs(lineItem.displayProductAttributes),
  };

  trackOrderAction(
    LEGACY_EDIT_DOCUMENT_URL_CONVERSION_SUCCESS_EVENT_NAME,
    additionalData
  );
}

/**
 * Fire an event that states this orderNumber was attempted to be bought now, with these lineItems being part of
 * that attempt.
 *
 * This method will fire an event for EACH lineItem ID
 * @param {string} orderNum
 * @param {array} reorderItems
 * @param {string} pageName the name of the page to include in tracking details
 */
export function expressCheckoutBuyNowAttempted(
  orderNum,
  reorderItems,
  pageName
) {
  if (reorderItems) {
    reorderItems.forEach(reorderItem => {
      const { productId, displayPosition } = reorderItem;
      const eventData = {
        category: pageName,
        label: productId,
        eventDetail: `${productId}:${displayPosition}`,
        navigationDetail: 'Buy now',
      };
      trackOrderAction(BUTTON_CLICKED_EVENT_NAME, eventData);
    });
  }
}

/**
 * Fire an event that tracks when a user clicks the buy now button
 * occurs after the item has been added to cart successfully.
 *
 * @param {string} lineItemId - line item id of item that is to be bought
 * @param {string} displayName - display name of the item that is to be bought
 * @param {string} productKey - product key of item that is to be bought
 * @param {string} page - name of the page the action is occurring on
 */
export function trackExpressCheckoutProductAddedToCart({
  lineItemId,
  displayName,
  productKey,
  pageName,
}) {
  const additionalData = {
    label: PRODUCT_REORDERED_LABEL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName,
    product_id: lineItemId,
    name: displayName,
    core_product_id: productKey,
  };

  trackOrderAction(EXPRESS_CHECKOUT_ADDED_TO_CART_EVENT_NAME, additionalData);
}

/**
 * Fire an event that tracks when the trackingUrl link is clicked
 *
 * @param {object} additionalData
 */
export function trackTrackingUrlClicked(pageName) {
  const additionalData = {
    label: TRACKING_URL_CLICKED_LABEL,
    navigationDetail: TRACKING_URL_LINK_DETAIL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName,
  };

  trackOrderAction(NAVIGATION_CLICKED_EVENT_NAME, additionalData);
}

// ModFather tracking support:
// ModFather expects an object with a 'trackEvent' function that takes an event.
// This adapter appends the route, which is needed by Segment, and the event
// name, which determines the Snowflake table that receives the event data.
const MOD_EVENT_NAME = 'UX_MOD_EVENT';
const MOD_ROUTE = 'om-frontend';

const trackEvent = event => {
  if (!event || !event.eventType) {
    return;
  }
  event.route = MOD_ROUTE;
  track(MOD_EVENT_NAME, event);
};
export const modfatherTracker = { trackEvent };
